import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { RichEditor } from './RichEditor';
import { Check, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Click-to-edit narrative-history cell, backed by the TipTap RichEditor.
 *
 * Same debounce-autosave contract as the inline `EditableField`:
 *  - 800ms after the last keystroke (longer than the spreadsheet cells —
 *    paragraphs need more typing breathing room).
 *  - On click anywhere on the static rendered HTML → swaps into edit mode.
 *  - On blur (clicking outside) → flushes the pending save and collapses
 *    back to read-only.
 *  - Optimistic cache update via TanStack Query.
 *
 * Permission gating: `canEdit=false` renders the sanitised HTML read-only,
 * no interaction.
 */
const DEBOUNCE_MS = 800;
const SAVED_PIP_MS = 1500;

type Status = 'idle' | 'saving' | 'saved' | 'error';

export function EditableRichField({
  clientId,
  fieldName,
  value,
  canEdit,
  placeholder,
}: {
  clientId: string;
  fieldName: string;
  value: string | null | undefined;
  canEdit: boolean;
  placeholder?: string;
}) {
  const qc = useQueryClient();
  const queryKey = useMemo(
    () => (['client', clientId, 'full'] as const),
    [clientId],
  );
  const initial = value ?? '';

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(initial);
  const [status, setStatus] = useState<Status>('idle');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Resync from server when not editing.
  useEffect(() => {
    if (!editing) setDraft(initial);
  }, [initial, editing]);

  // Cleanup on unmount.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const doSave = useCallback(async (raw: string) => {
    const prev = qc.getQueryData<unknown>(queryKey);
    qc.setQueryData(queryKey, (cur: unknown) => patchCachedClient(cur, fieldName, raw));

    setStatus('saving');
    try {
      await api(`/api/clients/${clientId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [fieldName]: raw }),
      });
      setStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus('idle'), SAVED_PIP_MS);
      void qc.invalidateQueries({ queryKey });
    } catch (e) {
      qc.setQueryData(queryKey, prev);
      setStatus('error');
      // The inline error pip is the user signal — no console noise.
      void e;
    }
  }, [clientId, fieldName, qc, queryKey]);

  const scheduleSave = useCallback((raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void doSave(raw); }, DEBOUNCE_MS);
  }, [doSave]);

  const flushSave = useCallback((raw: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (raw !== initial) {
      void doSave(raw);
    }
  }, [doSave, initial]);

  // Click-outside collapses + flushes. Declared after flushSave so it can
  // be listed in deps without violating react-hooks/exhaustive-deps.
  useEffect(() => {
    if (!editing) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        flushSave(draft);
        setEditing(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [editing, draft, flushSave]);

  function onChange(html: string) {
    setDraft(html);
    scheduleSave(html);
  }

  if (!canEdit) {
    return (
      <div
        className="text-xs prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: initial || '<span class="text-ink-mute italic">—</span>' }}
      />
    );
  }

  if (!editing) {
    const isEmpty = !initial || initial.replace(/<[^>]*>/g, '').trim() === '';
    return (
      <div
        ref={wrapRef}
        onClick={() => setEditing(true)}
        className="text-xs prose prose-sm max-w-none cursor-text border border-transparent hover:border-hairline rounded p-2 transition-colors min-h-[2rem]"
        title="Click to edit"
      >
        {isEmpty ? (
          <span className="text-ink-mute italic">{placeholder ?? 'Click to add…'}</span>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: initial }} />
        )}
        <StatusPip status={status} />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <RichEditor value={draft} onChange={onChange} placeholder={placeholder} />
      <div className="absolute top-1 right-2"><StatusPip status={status} /></div>
    </div>
  );
}

function StatusPip({ status }: { status: Status }) {
  if (status === 'saving') return <Loader2 size={10} className="animate-spin text-ink-mute inline ml-2" aria-label="Saving" />;
  if (status === 'saved')  return <Check size={10} className="text-emerald-600 inline ml-2" aria-label="Saved" />;
  if (status === 'error')  return <AlertTriangle size={10} className="text-rose-600 inline ml-2" aria-label="Save failed" />;
  return null;
}

function patchCachedClient(cur: unknown, fieldName: string, value: unknown): unknown {
  if (!cur || typeof cur !== 'object') return cur;
  const obj = cur as { data?: { client?: Record<string, unknown> } };
  if (!obj.data?.client) return cur;
  return {
    ...obj,
    data: { ...obj.data, client: { ...obj.data.client, [fieldName]: value } },
  };
}
